// src/hooks/useContract.js (최종 수정된 버전)

import { useState, useCallback } from 'react';
import {
  createTrade as createTradeContract,
  submitPaymentProof as submitPaymentProofContract,
  confirmPayment as confirmPaymentContract,
  cancelTrade as cancelTradeContract,
  getTrade,
  getUserTrades,
  setFeePercent as setFeePercentContract,
  getCurrentFeePercent,
  getCurrentFeePercentage
} from '../utils/contract';

export const useContract = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userTrades, setUserTrades] = useState([]);

  // 수수료율 설정
  const setFeePercent = useCallback(async (newFeePercent) => {
    setLoading(true);
    setError(null);

    try {
      await setFeePercentContract(newFeePercent);
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // 현재 수수료율 조회
  const getFeeInfo = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [feePercent, feePercentage] = await Promise.all([
        getCurrentFeePercent(),
        getCurrentFeePercentage()
      ]);
      return { feePercent, feePercentage };
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // 거래 생성
  const createTrade = useCallback(async (buyerAddress, wlcAmount, krwAmount, durationHours) => {
    setLoading(true);
    setError(null);

    try {
      const tradeId = await createTradeContract(buyerAddress, wlcAmount, krwAmount, durationHours);
      return tradeId;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // 송금 증명 제출
  const submitPaymentProof = useCallback(async (tradeId, file) => {
    setLoading(true);
    setError(null);

    try {
      // 파일 해시 계산
      const { calculateFileHash } = await import('../utils/contract');
      const fileHash = await calculateFileHash(file);
      
      // 블록체인에 해시 저장
      await submitPaymentProofContract(tradeId, fileHash);
      
      // 로컬 스토리지에 파일 저장
      const reader = new FileReader();
      reader.onload = () => {
        localStorage.setItem(`proof_${tradeId}`, reader.result);
        localStorage.setItem(`proof_${tradeId}_filename`, file.name);
      };
      reader.readAsDataURL(file);
      
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // 거래 승인
  const confirmPayment = useCallback(async (tradeId) => {
    setLoading(true);
    setError(null);

    try {
      await confirmPaymentContract(tradeId);
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // 거래 취소
  const cancelTrade = useCallback(async (tradeId, reason) => {
    setLoading(true);
    setError(null);

    try {
      await cancelTradeContract(tradeId, reason);
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // 사용자 거래 목록 로드
  const loadUserTrades = useCallback(async (userAddress) => {
    if (!userAddress) return;

    setLoading(true);
    setError(null);

    try {
      const trades = await getUserTrades(userAddress);
      setUserTrades(trades);
    } catch (error) {
      setError(error.message);
      console.error('거래 목록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 특정 거래 정보 가져오기
  const getTradeInfo = useCallback(async (tradeId) => {
    setLoading(true);
    setError(null);

    try {
      const trade = await getTrade(tradeId);
      return trade;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // 로컬 저장된 송금 증명 가져오기
  const getStoredPaymentProof = useCallback((tradeId) => {
    const proof = localStorage.getItem(`proof_${tradeId}`);
    const filename = localStorage.getItem(`proof_${tradeId}_filename`);
    
    if (proof && filename) {
      return { proof, filename };
    }
    
    return null;
  }, []);

  // 에러 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    userTrades,
    createTrade,
    submitPaymentProof,
    confirmPayment,
    cancelTrade,
    loadUserTrades,
    getTradeInfo,
    getStoredPaymentProof,
    setFeePercent,
    getFeeInfo,
    clearError
  };
};